import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connectorTypeSettingsService } from '../services/connectorTypeSettingsService';
import { connectorTypesService } from '../services/connectorTypesService';
import './ConnectorTypeSettingForm.css';

const ConnectorTypeSettingForm = ({ mode = 'create' }) => {
  const [formData, setFormData] = useState({
    v2_connector_type_id: '',
    name: ''
  });
  const [connectorType, setConnectorType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();
  const { connectorTypeId, id } = useParams();

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    loadConnectorType();
    if (id && (isViewMode || isEditMode)) {
      loadSetting();
    } else {
      setFormData(prev => ({
        ...prev,
        v2_connector_type_id: connectorTypeId
      }));
      setInitialLoading(false);
    }
  }, [id, connectorTypeId]);

  const loadConnectorType = async () => {
    try {
      const response = await connectorTypesService.get(connectorTypeId);
      setConnectorType(response.connectorType);
    } catch (err) {
      console.error('Error loading connector type:', err);
    }
  };

  const loadSetting = async () => {
    try {
      setInitialLoading(true);
      const response = await connectorTypeSettingsService.get(id);
      setFormData({
        v2_connector_type_id: response.connectorTypeSetting.v2_connector_type_id,
        name: response.connectorTypeSetting.name
      });
    } catch (err) {
      console.error('Error loading setting:', err);
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isCreateMode) {
        await connectorTypeSettingsService.create(formData);
      } else if (isEditMode) {
        await connectorTypeSettingsService.update(id, {
          name: formData.name
        });
      }

      navigate(`/admin/connector-types/${connectorTypeId}/settings`);
    } catch (err) {
      console.error('Error saving setting:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/connector-types/${connectorTypeId}/settings`);
  };

  if (initialLoading) {
    return (
      <div className="connector-type-setting-form-page">
        <div className="loading">Loading setting...</div>
      </div>
    );
  }

  return (
    <div className="connector-type-setting-form-page">
      <div className="form-header">
        <h1>
          {isCreateMode && 'Create Connector Type Setting'}
          {isEditMode && 'Edit Connector Type Setting'}
          {isViewMode && 'View Connector Type Setting'}
        </h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-container">
        <button
          type="button"
          className="btn btn-link back-button"
          onClick={handleCancel}
        >
          ← Back to Settings
        </button>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              Connector Type
            </label>
            <div className="form-static-value">
              {connectorType?.name}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode || loading}
              required
              placeholder="Enter setting name"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
              </button>
            )}
            {isViewMode && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/admin/connector-types/${connectorTypeId}/settings/${id}/edit`)}
              >
                Edit
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectorTypeSettingForm;
